import React from 'react';

import { Alert, Button, Group, LoadingOverlay, Stack, Text, px, useMantineTheme } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import useDeletePackage from 'hooks/package/useDeletePackage';
import usePackage from 'hooks/package/usePackage';
import notificationHandler from 'utils/notifications';

interface IDeletePackageModalProps {
  packageId: string;
  handleClose: () => void;
}

const DeletePackageModal = ({ packageId, handleClose }: IDeletePackageModalProps): JSX.Element => {
  const theme = useMantineTheme();

  const { data: packageData, isLoading, isSuccess, isError } = usePackage(packageId);
  const deletePackageMutation = useDeletePackage();

  const handleDelete = (): void => {
    const { successNotification, errorNotification } = notificationHandler();
    deletePackageMutation.mutate(packageId, {
      onSuccess: () => {
        handleClose();
        successNotification('Package delete success', `${packageData?.data.name} successfully deleted.`);
      },
      onError: error => {
        handleClose();
        errorNotification('Package delete failed', error.response?.data.message);
      },
    });
  };

  return (
    <Stack>
      <LoadingOverlay visible={isLoading} />
      {isSuccess && (
        <>
          <Text>{`Are you sure you want to delete ${packageData?.data.name}?`}</Text>
          <Text color='dimmed'>Deleting this package will not affect reporting data.</Text>
          <Group position='right'>
            <Button
              onClick={handleDelete}
              color='red'
              loading={deletePackageMutation.isLoading}
              data-cy='confirmDeletePackage'
            >
              Delete
            </Button>
          </Group>
        </>
      )}
      {isError && (
        <Alert icon={<IconExclamationCircle size={px(theme.fontSizes.md)} />} title='Error' color='red'>
          Something went wrong while fetching data!
        </Alert>
      )}
    </Stack>
  );
};

export default DeletePackageModal;
